import { useCallback, useState } from 'react';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useConnectCustomWallet from 'src/hooks/wallet-management/useConnectCustomWallet';
import { useDisconnectWallets } from 'src/hooks/wallets/useDisconnectWallets';
import { notify } from 'src/utils/notifications';
import { ONBOARDING_CONNECTION_STEP_LOCK_KEY } from 'src/utils/WalletConnectionState';
import usePrivyEmailAuth from 'src/components/Modals/Onboarding/ConnectWalletModal/PrivyEmailOtpPage/hooks/usePrivyEmailAuth';
import { DRIFT_CUSTOM_WALLET_OPTIONS } from 'src/constants/wallets';

interface Params {
	email: string;
	resendSecondsRemaining: number;
	setIsOtpSent: (isOtpSent: boolean) => void;
}

export default function usePrivyEmailOtpPage({
	email,
	resendSecondsRemaining,
	setIsOtpSent,
}: Params) {
	const connected = useWalletIsConnected();
	const disconnectWallets = useDisconnectWallets();
	const { connectWithCustomWallet } = useConnectCustomWallet();
	const walletConnectionState = useCurrentWalletConnectionState();

	const { code, setCode, sendCode, verifyCodeAndEnsureEmbeddedWallet } =
		usePrivyEmailAuth();

	const [isSendingCode, setIsSendingCode] = useState(false);

	const isResendButtonDisabled = resendSecondsRemaining > 0 || isSendingCode;

	const [isVerifyingCode, setIsVerifyingCode] = useState(false);

	const handleSendCode = useCallback(async () => {
		if (isResendButtonDisabled) {
			return;
		}

		try {
			setIsSendingCode(true);

			await sendCode({ email });

			setIsOtpSent(true);

			notify({
				type: 'success',
				message: 'Verification code sent to your email.',
			});
		} catch (error) {
			console.error(error);

			notify({
				type: 'error',
				message: 'Failed to send verification code.',
			});
		} finally {
			setIsSendingCode(false);
		}
	}, [isResendButtonDisabled, sendCode, email]);

	const handleLoginWithCode = useCallback(async () => {
		try {
			if (connected) {
				await disconnectWallets();
			}

			setIsVerifyingCode(true);

			const embeddedWallet = await verifyCodeAndEnsureEmbeddedWallet();

			walletConnectionState.instance.update(
				'Connecting',
				true,
				ONBOARDING_CONNECTION_STEP_LOCK_KEY
			);

			await connectWithCustomWallet(
				DRIFT_CUSTOM_WALLET_OPTIONS.PRIVY_NATIVE_WALLET_ADAPTER_NAME,
				{ embeddedWallet }
			);

			notify({
				type: 'success',
				message: 'Connected with Privy email.',
			});
		} catch (error) {
			console.error(error);

			walletConnectionState.instance.update(
				'Connecting',
				false,
				ONBOARDING_CONNECTION_STEP_LOCK_KEY
			);

			notify({
				type: 'error',
				message: 'Failed to connect with Privy email',
			});
		} finally {
			setIsVerifyingCode(false);
		}
	}, [
		connected,
		disconnectWallets,
		verifyCodeAndEnsureEmbeddedWallet,
		walletConnectionState,
		connectWithCustomWallet,
	]);

	return {
		code,
		setCode,
		handleSendCode,
		handleLoginWithCode,
		isSendingCode,
		isVerifyingCode,
		resendSecondsRemaining,
		isResendButtonDisabled,
	};
}
